import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, X, ArrowRight } from "lucide-react";
import { buildCompletionUrl, hasFeedbackSubmitted } from "@/lib/userInterviews";

/**
 * Persistent purple header CTA for User Interviews testers.
 * If the tester hasn't submitted the in-app survey yet, clicking opens a
 * confirm modal nudging them to /feedback first. Once they've submitted
 * (markFeedbackSubmitted is called from Feedback.jsx), clicking the button
 * goes straight to the User Interviews completion URL.
 */
export default function FinishStudyButton({ iid }) {
    const [open, setOpen] = useState(false);
    const completionUrl = buildCompletionUrl(iid);
    const alreadySubmitted = hasFeedbackSubmitted(iid);

    if (!completionUrl) return null;

    const onClick = (e) => {
        if (alreadySubmitted) return; // let the <a> navigate normally
        e.preventDefault();
        setOpen(true);
    };

    return (
        <>
            <a
                href={completionUrl}
                onClick={onClick}
                data-testid="layout-finish-study"
                title={alreadySubmitted
                    ? "Wrap up your study session"
                    : "We'd love your feedback before you go"}
                className="inline-flex items-center gap-1.5 bg-stage_late-dark text-white hover:bg-stage_late-dark/90 rounded-2xl px-3.5 py-2 text-sm font-medium"
            >
                <CheckCircle2 size={14} strokeWidth={1.8} />
                <span className="hidden sm:inline">Finish study</span>
            </a>

            {open && (
                <div
                    className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
                    data-testid="finish-study-modal"
                    onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
                >
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 md:p-7">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                                <h2 className="text-xl font-heading font-semibold tracking-tight">
                                    Please — your survey is how the next caregiver gets help.
                                </h2>
                                <p className="mt-2 text-sm text-[#4C5551] leading-relaxed">
                                    Every answer goes straight into the next version of Memory Lane.
                                    The next daughter searching at 2 AM for how to handle her dad&apos;s
                                    wandering. The next son who can&apos;t get his sister on the same page
                                    about Mom&apos;s care. They get a better tool because of what you
                                    share in the next 3 minutes.
                                </p>
                                <p className="mt-2 text-sm text-[#1F2624] font-medium">
                                    Please don&apos;t skip this. You&apos;re the reason it gets better.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                                data-testid="finish-study-close"
                                className="shrink-0 w-8 h-8 rounded-full hover:bg-cream-dark flex items-center justify-center"
                            >
                                <X size={16} strokeWidth={1.8} />
                            </button>
                        </div>

                        <Link
                            to="/feedback"
                            data-testid="finish-study-take-survey"
                            onClick={() => setOpen(false)}
                            className="mt-4 inline-flex items-center justify-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-5 py-3 text-sm font-medium w-full"
                        >
                            Yes — take me to the 3-min survey
                            <ArrowRight size={14} strokeWidth={2} />
                        </Link>

                        <p className="mt-3 text-xs text-[#6E7A74] text-center">
                            The survey is the only way to wrap up — thank you for sticking with it.
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
